/**
 * Shared plumbing for the render pipeline: fullscreen blitting, render-target
 * helpers, deterministic blue noise, procedural lens dirt, GLSL common chunk.
 *
 * owner: render
 */
import * as THREE from 'three';
import { makeRng } from '../core/Rand.js';

/* ------------------------------------------------------------------ *
 * Fullscreen triangle
 * ------------------------------------------------------------------ */

const _triGeo = new THREE.BufferGeometry();
_triGeo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
_triGeo.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 2, 0, 0, 2]), 2));
_triGeo.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 0, 1, 0, 0, 1]), 3));

export const FS_VERT = /* glsl */`
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4( position.xy, 0.0, 1.0 );
}
`;

/** Draws a fullscreen triangle with an arbitrary material into a target. */
export class Blitter {
  constructor() {
    this.scene = new THREE.Scene();
    this.scene.matrixAutoUpdate = false;
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.mesh = new THREE.Mesh(_triGeo, null);
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.scene.add(this.mesh);
  }

  /** @param {THREE.WebGLRenderer} renderer @param {THREE.Material} material @param {THREE.WebGLRenderTarget|null} target */
  draw(renderer, material, target = null, clear = false) {
    this.mesh.material = material;
    const prevAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.setRenderTarget(target);
    if (clear) renderer.clear(true, false, false);
    renderer.render(this.scene, this.camera);
    renderer.autoClear = prevAutoClear;
  }

  dispose() {
    this.scene.clear();
  }
}

/** ShaderMaterial with the fullscreen vertex shader and post-friendly state. */
export function makeMaterial({ fragmentShader, uniforms = {}, defines = {}, glsl3 = false, name = 'postfx' }) {
  const m = new THREE.ShaderMaterial({
    name,
    uniforms,
    defines,
    vertexShader: FS_VERT,
    fragmentShader,
    depthTest: false,
    depthWrite: false,
    blending: THREE.NoBlending,
    toneMapped: false,
    fog: false,
  });
  if (glsl3) m.glslVersion = THREE.GLSL3;
  return m;
}

/* ------------------------------------------------------------------ *
 * Render targets
 * ------------------------------------------------------------------ */

export function makeRT(w, h, opts = {}) {
  const rt = new THREE.WebGLRenderTarget(Math.max(1, w | 0), Math.max(1, h | 0), {
    type: opts.type ?? THREE.HalfFloatType,
    format: opts.format ?? THREE.RGBAFormat,
    minFilter: opts.minFilter ?? THREE.LinearFilter,
    magFilter: opts.magFilter ?? THREE.LinearFilter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    depthBuffer: opts.depthBuffer ?? false,
    stencilBuffer: false,
    generateMipmaps: false,
    samples: opts.samples ?? 0,
    count: opts.count ?? 1,
  });
  for (const t of rt.textures) {
    t.name = opts.name ?? 'postfx.rt';
    t.colorSpace = THREE.NoColorSpace;
  }
  return rt;
}

/* ------------------------------------------------------------------ *
 * Sampling sequences
 * ------------------------------------------------------------------ */

/** Radical-inverse Halton. */
export function halton(index, base) {
  let f = 1, r = 0, i = index;
  while (i > 0) {
    f /= base;
    r += f * (i % base);
    i = Math.floor(i / base);
  }
  return r;
}

/** Halton(2,3) jitter table in the range [-0.5, 0.5] pixels. */
export function haltonJitterTable(count) {
  const out = [];
  for (let i = 1; i <= count; i++) out.push([halton(i, 2) - 0.5, halton(i, 3) - 0.5]);
  return out;
}

/* ------------------------------------------------------------------ *
 * Deterministic blue noise (void-and-cluster)
 * ------------------------------------------------------------------ */

function voidAndCluster(N, seed) {
  const n = N * N;
  const rng = makeRng(seed >>> 0);
  const bin = new Uint8Array(n);
  const energy = new Float32Array(n);
  const rank = new Int32Array(n).fill(-1);

  // Gaussian energy kernel (wrapped)
  const R = 5, sigma = 1.9;
  const kdx = [], kdy = [], kw = [];
  for (let dy = -R; dy <= R; dy++) {
    for (let dx = -R; dx <= R; dx++) {
      if (dx === 0 && dy === 0) { kdx.push(0); kdy.push(0); kw.push(1.0); continue; }
      kdx.push(dx); kdy.push(dy); kw.push(Math.exp(-(dx * dx + dy * dy) / (2 * sigma * sigma)));
    }
  }
  const K = kw.length;

  const splat = (idx, s) => {
    const x = idx % N, y = (idx / N) | 0;
    for (let k = 0; k < K; k++) {
      const xx = (x + kdx[k] + N) % N;
      const yy = (y + kdy[k] + N) % N;
      energy[yy * N + xx] += s * kw[k];
    }
  };
  const tightestCluster = () => {
    let bi = -1, be = -Infinity;
    for (let i = 0; i < n; i++) if (bin[i] === 1 && energy[i] > be) { be = energy[i]; bi = i; }
    return bi;
  };
  const largestVoid = () => {
    let bi = -1, be = Infinity;
    for (let i = 0; i < n; i++) if (bin[i] === 0 && energy[i] < be) { be = energy[i]; bi = i; }
    return bi;
  };

  // seed with a scattered initial pattern
  const order = new Int32Array(n);
  for (let i = 0; i < n; i++) order[i] = i;
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const t = order[i]; order[i] = order[j]; order[j] = t;
  }
  let M = Math.max(4, Math.round(n / 10));
  for (let i = 0; i < M; i++) { bin[order[i]] = 1; splat(order[i], 1); }

  // relax into a proper blue-noise prototype
  for (let it = 0; it < 512; it++) {
    const c = tightestCluster();
    bin[c] = 0; splat(c, -1);
    const v = largestVoid();
    if (v === c) { bin[c] = 1; splat(c, 1); break; }
    bin[v] = 1; splat(v, 1);
  }

  const proto = bin.slice();
  const protoEnergy = energy.slice();

  // phase 1 — remove the prototype's points, tightest cluster first
  let count = M;
  while (count > 0) {
    const c = tightestCluster();
    if (c < 0) break;
    bin[c] = 0; splat(c, -1);
    count--;
    rank[c] = count;
  }

  // phase 2 + 3 — refill from the prototype, largest void first
  bin.set(proto);
  energy.set(protoEnergy);
  for (let r = M; r < n; r++) {
    const v = largestVoid();
    if (v < 0) break;
    bin[v] = 1; splat(v, 1);
    rank[v] = r;
  }

  const out = new Uint8Array(n);
  for (let i = 0; i < n; i++) out[i] = Math.min(255, Math.max(0, Math.round((rank[i] + 0.5) * 255 / n)));
  return out;
}

let _blueNoiseCache = null;

/**
 * 64x64 RGBA blue-noise tile. R/G are two independent void-and-cluster fields,
 * B/A are the same fields toroidally offset (cheap decorrelation).
 */
export function makeBlueNoiseTexture(size = 64) {
  if (_blueNoiseCache) return _blueNoiseCache;
  const a = voidAndCluster(size, 0x1a2b3c4d);
  const b = voidAndCluster(size, 0x51ed270f);
  const n = size * size;
  const data = new Uint8Array(n * 4);
  const off = (arr, ox, oy, i) => {
    const x = (i % size + ox) % size;
    const y = (((i / size) | 0) + oy) % size;
    return arr[y * size + x];
  };
  for (let i = 0; i < n; i++) {
    data[i * 4 + 0] = a[i];
    data[i * 4 + 1] = b[i];
    data[i * 4 + 2] = off(a, (size >> 1) + 3, (size / 3) | 0, i);
    data[i * 4 + 3] = off(b, (size / 3) | 0, (size >> 1) + 7, i);
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.name = 'postfx.blueNoise';
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = tex.magFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  _blueNoiseCache = tex;
  return tex;
}

/* ------------------------------------------------------------------ *
 * Procedural lens dirt
 * ------------------------------------------------------------------ */

/** Smudges, dust motes and a couple of hairline scratches. Deterministic. */
export function makeLensDirtTexture(size = 512) {
  const rng = makeRng(0x0d1e7a17);
  const n = size * size;
  const buf = new Float32Array(n);

  const blob = (cx, cy, r, amp, squash) => {
    const x0 = Math.max(0, Math.floor(cx - r)), x1 = Math.min(size - 1, Math.ceil(cx + r));
    const y0 = Math.max(0, Math.floor(cy - r / squash)), y1 = Math.min(size - 1, Math.ceil(cy + r / squash));
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        const dx = (x - cx) / r, dy = (y - cy) * squash / r;
        const d = dx * dx + dy * dy;
        if (d > 1) continue;
        const f = 1 - d;
        buf[y * size + x] += amp * f * f;
      }
    }
  };

  // greasy smudges
  for (let i = 0; i < 26; i++) {
    blob(rng() * size, rng() * size, rng.range(24, 90), rng.range(0.15, 0.5), rng.range(0.5, 2.2));
  }
  // dust specks
  for (let i = 0; i < 900; i++) {
    blob(rng() * size, rng() * size, rng.range(1.0, 4.5), rng.range(0.3, 1.4), rng.range(0.7, 1.4));
  }
  // hairline scratches
  for (let i = 0; i < 7; i++) {
    let x = rng() * size, y = rng() * size;
    const ang = rng() * Math.PI * 2;
    const len = rng.range(60, 260);
    const dx = Math.cos(ang), dy = Math.sin(ang);
    for (let t = 0; t < len; t++) {
      blob(x + dx * t + rng.gauss(0, 0.6), y + dy * t + rng.gauss(0, 0.6), rng.range(0.9, 1.8), 0.35, 1);
    }
  }

  const data = new Uint8Array(n * 4);
  for (let i = 0; i < n; i++) {
    const v = Math.min(1, buf[i]);
    // slightly warm, slightly uneven per channel so dirt does not read as grey
    data[i * 4 + 0] = Math.round(255 * Math.min(1, v * 1.06));
    data[i * 4 + 1] = Math.round(255 * v);
    data[i * 4 + 2] = Math.round(255 * Math.min(1, v * 0.9));
    data[i * 4 + 3] = 255;
  }
  const tex = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.name = 'postfx.lensDirt';
  tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** 1x1 white texture — neutral stand-in for AO / SSR when a pass is disabled. */
export function makeFlatTexture(r, g, b, a) {
  const tex = new THREE.DataTexture(new Uint8Array([r, g, b, a]), 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.needsUpdate = true;
  tex.colorSpace = THREE.NoColorSpace;
  tex.minFilter = tex.magFilter = THREE.NearestFilter;
  return tex;
}

/* ------------------------------------------------------------------ *
 * GLSL shared chunk
 * ------------------------------------------------------------------ */

export const GLSL_COMMON = /* glsl */`
#ifndef saturate
#define saturate(a) clamp(a, 0.0, 1.0)
#endif
#define PI_ 3.141592653589793
#define HALF_PI_ 1.5707963267948966

float pfLuma(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

/* Scrub NaN/Inf. Buffers produced by other systems land in this pipeline, and a
   single non-finite texel poisons the histogram (log2 of NaN) and then the
   exposure of the whole frame. Cheap insurance at every hand-off point. */
vec3 pfSanitize(vec3 c) {
  bvec3 bad = bvec3(isnan(c.x) || isinf(c.x), isnan(c.y) || isinf(c.y), isnan(c.z) || isinf(c.z));
  return mix(max(c, vec3(0.0)), vec3(0.0), vec3(bad));
}
float maxc(vec3 c) { return max(c.r, max(c.g, c.b)); }

vec3 rgb2ycocg(vec3 c) {
  return vec3(
    0.25 * c.r + 0.5 * c.g + 0.25 * c.b,
    0.5 * c.r - 0.5 * c.b,
    -0.25 * c.r + 0.5 * c.g - 0.25 * c.b);
}
vec3 ycocg2rgb(vec3 c) {
  float t = c.x - c.z;
  return vec3(t + c.y, c.x + c.z, t - c.y);
}

/* window depth [0,1] -> view space position (right handed, -z forward) */
vec3 viewFromDepth(vec2 uv, float d01, mat4 invProj) {
  vec4 clip = vec4(uv * 2.0 - 1.0, d01 * 2.0 - 1.0, 1.0);
  vec4 v = invProj * clip;
  return v.xyz / v.w;
}
float linearizeDepth(float d01, float n, float f) {
  float z = d01 * 2.0 - 1.0;
  return (2.0 * n * f) / (f + n - z * (f - n));
}

/* roughness/metalness packing used by the geometry prepass */
float unpackRough(float p) { return fract(p * 0.5) * 2.0; }
float unpackMetal(float p) { return step(1.0, p); }

vec3 srgbEncode(vec3 c) {
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(0.41666)) - 0.055, step(0.0031308, c));
}
`;
